import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { LayoutManagerComponent } from '@helios/layout-manager'
import { PanelCategory, PanelMetadata } from '@helios/panel-base'
import { DragSource } from 'golden-layout'
import { groupBy } from 'lodash-es'
import { Observable } from 'rxjs'
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators'
import { PanelCardComponent } from './panel-card/panel-card.component'

interface PanelCategoryMetadata {
  expanded: boolean
  hasVisiblePanels: boolean
  panels: Array<PanelMetadata & { isVisible?: boolean }>
}
interface PanelsByCategory {
  [panelCategory: string]: PanelCategoryMetadata
}

@Component({
  selector: 'hs-panel-catalog',
  templateUrl: './panel-catalog.component.html',
  styleUrls: ['./panel-catalog.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    'aria-hidden': 'true',
  },
})
export class PanelCatalogComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() panelMetadata: (PanelMetadata & { isVisible?: boolean })[] = []
  @Input() layoutManager?: LayoutManagerComponent
  @Output() closePanel = new EventEmitter<void>()
  @Output() dragSourcesCreated = new EventEmitter<void>()

  @ViewChildren(PanelCardComponent) panelCards?: QueryList<PanelCardComponent>

  panelsByCategory?: PanelsByCategory

  private readonly dragSources: DragSource[] = []

  panelSearch = new FormControl('')

  visiblePanels$: Observable<string[]> = this.panelSearch.valueChanges.pipe(
    startWith(''),
    distinctUntilChanged(),
    map((query: string) => this.searchPanels(query)),
    tap((panelKeys: string[]) => this.setPanelVisibilityByKeys(panelKeys)),
  )

  constructor() {
    this.visiblePanels$.subscribe()
  }

  setPanelVisibility() {
    // Otherwise go by category
    this.panelMetadata.forEach(panel => {
      if (!this.panelsByCategory?.[panel.category]) {
        return
      }

      const category = this.panelsByCategory[panel.category]

      category.hasVisiblePanels = category.expanded
      category.panels.forEach(p => {
        p.isVisible = category.expanded
      })
    })
  }

  setPanelVisibilityByKeys(panelKeys: string[] = []) {
    this.panelMetadata.forEach(panel => {
      panel.isVisible = panelKeys.includes(panel.key)
    })
  }

  ngOnInit() {
    this.panelsByCategory = Object.entries(
      groupBy(this.panelMetadata, 'category'),
    ).reduce<PanelsByCategory>((result, [key, panels]) => {
      result[key] = {
        expanded: true,
        panels,
        hasVisiblePanels: false,
      }
      return result
    }, {})

    this.setPanelVisibility()
  }

  ngOnDestroy() {
    this.layoutManager?.removeDragSources(this.dragSources)
  }

  ngAfterViewInit() {
    this.panelCards?.forEach(panel => {
      if (panel.panelMetadata != null && panel.dragAndDrop != null) {
        const dragSource = this.layoutManager?.loadDragSource(
          panel.panelMetadata.key,
          panel.dragAndDrop,
          panel.panelMetadata.title,
        )
        if (dragSource) {
          this.dragSources.push(dragSource)
        }
      }
    })
    this.dragSourcesCreated.emit()
  }

  /**
   * @internal
   * @param query search terms
   * @returns  keys of matching panels
   */
  searchPanels(query: string): string[] {
    if (query === '') return this.panelMetadata.map(x => x.key)
    const lowerCaseQuery = query.toLowerCase()
    return this.panelMetadata
      .map(p => ({
        ...p,
        title: p.title.toLowerCase(),
        description: p.description.toLowerCase(),
      }))
      .filter(
        p =>
          p.title.includes(lowerCaseQuery) ||
          p.description.includes(lowerCaseQuery),
      )
      .map(p => p.key)
  }

  toggleCategory(category: PanelCategory | string) {
    const c = this.panelsByCategory?.[category as unknown as string]
    if (c) {
      // we cannot use immutability because the panels dom need to never get recreated
      c.expanded = !c.expanded
    }
  }

  // ngFor by defaults sorts the array
  // If you do keyValue: 0 in the html for the ngFor pipe you get an error:
  // 'TypeError: the comparison function must be either a function or undefined'
  // This is necessary to avoid it.
  // https://medium.com/gammastack/angular-keyvalue-sorting-and-variation-of-ngfor-directive-be57701bfb61
  /*@internal*/
  returnZero() {
    return 0
  }
}
